import { IJob } from 'src/models';
import { IReportRejectSelectionConfig } from 'src/models/Report';
import * as api from 'src/api';
import { isElectron } from 'src/utils/miscUtils';
import { getCurrentLocalReportContext } from 'src/utils/localReportContext';

type PrintThermalReportArgs = {
  jobId?: string;
  jobJson?: unknown;
  url?: string;
  locale?: string;
  reportContext?: unknown;
  rejectionCalculationConfig?: IReportRejectSelectionConfig;
};

const canUseThermalPrint = () => {
  return isElectron() && typeof window !== 'undefined' && (window as any).electron?.printThermalReport;
};

const buildFreshReportRequestParams = (
  jobId: string,
  locale: string,
  format: 'thermal_58mm' | 'json',
  rejectionCalculationConfig?: IReportRejectSelectionConfig
) => {
  const reportRefreshToken = `${Date.now()}`;
  return {
    reportRefreshToken,
    reportParams: {
      jobId,
      deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata",
      locale,
      output_format: format,
      rejectionCalculationConfig,
      forceRegenerate: true,
      reportRefreshToken
    }
  };
};

const appendCacheBuster = (url: string, token: string) => {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('reportRefreshToken', token);
    return parsed.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}reportRefreshToken=${encodeURIComponent(token)}`;
  }
};

export const printThermalReport58mm = async (
  job: IJob | null | undefined,
  locale = 'en',
  rejectionCalculationConfig?: IReportRejectSelectionConfig
) => {
  if (!job?.jobId) return;

  if (!canUseThermalPrint()) {
    throw new Error('Thermal printing is only available in the desktop (Electron) app.');
  }

  const effectiveRejectionCalculationConfig = rejectionCalculationConfig
    || (job?.results?.rejectionCalculationConfig as IReportRejectSelectionConfig | undefined);

  // Local jobs: generate + print via local report generator.
  if (job.processingMode === 'local') {
    const args: PrintThermalReportArgs = {
      jobId: job.jobId,
      reportContext: getCurrentLocalReportContext(),
      locale,
      rejectionCalculationConfig: effectiveRejectionCalculationConfig,
    };
    await (window as any).electron.printThermalReport(args);
    return;
  }

  // Cloud jobs: prefer requesting a server-generated thermal report (requires backend support).
  try {
    const thermalRequest = buildFreshReportRequestParams(
      job.jobId,
      locale,
      'thermal_58mm',
      effectiveRejectionCalculationConfig
    );
    const response = await api.getReport({
      report_type: "job_report",
      report_params: thermalRequest.reportParams
    });

    const reportUrl = response?.report_url;
    if (!reportUrl) {
      throw new Error('Missing report URL');
    }

    const args: PrintThermalReportArgs = { url: appendCacheBuster(reportUrl, thermalRequest.reportRefreshToken) };
    await (window as any).electron.printThermalReport(args);
    return;
  } catch {
    const jsonRequest = buildFreshReportRequestParams(
      job.jobId,
      locale,
      'json',
      effectiveRejectionCalculationConfig
    );
    // Fallback: fetch JSON and let the main process print a basic wrapper.
    const response = await api.getReport({
      report_type: "job_report",
      report_params: jsonRequest.reportParams
    });

    const reportUrl = response?.report_url;
    if (!reportUrl) {
      throw new Error('Missing report URL');
    }

    const reportResp = await fetch(appendCacheBuster(reportUrl, jsonRequest.reportRefreshToken));
    if (!reportResp.ok) {
      throw new Error(`Failed to fetch report JSON (status ${reportResp.status})`);
    }

    const args: PrintThermalReportArgs = { jobJson: await reportResp.json() };
    await (window as any).electron.printThermalReport(args);
  }
};
